import { addToast, Table } from '@octano/global-ui';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Card, Col } from 'reactstrap';
import TableEmptyContent from '../../../../../../components/text/TableEmptyContent';
import { TranslationsKeys } from '../../../../../../locales/translations';
import { copySurveyDraft, deleteSurveyDraft } from '../../../api';
import { ActionType } from '../../SurveyCreate/hooks/useActions';
import useColumns from '../hooks/useColumns';
import ConfirmModal, { ConfirmModalRef } from './ConfirmModal';
import {
  ITEMS_PER_PAGE_DEFAULT,
  useSurveyListLoader,
} from './SurveyDraftListLoader';

function SurveyTable() {
  const { data, loading, refresh, query, updateQuery } = useSurveyListLoader();
  const { t } = useTranslation(TranslationsKeys.MANAGEMENT_TOOLS_SURVEY);
  const history = useHistory();
  const confirmRef = useRef<ConfirmModalRef>(null);
  const [confirmAction, setConfirmAction] = useState<ActionType | null>(null);
  const [confirmMessage, setConfirmMessage] = useState<string>('');

  const handleDeleteDraft = async (row: any) => {
    try {
      const resp = await deleteSurveyDraft(row.id);
      if (resp.error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t('surveyList.delete.error'),
        });
      } else {
        addToast({
          icon: 'success',
          color: 'success',
          text: t('surveyList.delete.success'),
        });
        refresh();
      }
    } catch {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('surveyList.delete.error'),
      });
    }
  };

  const handleCopyDraft = async (row: any) => {
    try {
      const resp = await copySurveyDraft(row.id);
      if (resp.error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t('surveyList.copy.draft.error'),
        });
      } else {
        addToast({
          icon: 'success',
          color: 'success',
          text: t('surveyList.copy.draft.success'),
        });
        refresh();
      }
    } catch {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('surveyList.copy.draft.error'),
      });
    }
  };

  const onConfirm = (row: any) => {
    if (confirmAction === 'delete') return handleDeleteDraft(row);
    if (confirmAction === 'copyDraft') return handleCopyDraft(row);
  };

  const columnsOthers = useColumns({
    onClickCopy: (row) => {
      setConfirmAction('copyDraft');
      setConfirmMessage(t('surveyList.copy.draft.confirmation'));
      confirmRef.current?.openModal(row);
    },
    onClickEdit: (row) => {
      history.push(`/surveys/update/draft/${row.id}`);
    },
    onClickRemove: (row) => {
      setConfirmAction('delete');
      setConfirmMessage(t('surveyList.delete.confirmation'));
      confirmRef.current?.openModal(row);
    },
  });

  return (
    <>
      <Card className="m-3 p-3">
        <div className="mb-3">
          <p className="fs-16 text-light mt-3">
            {t('surveyList.descriptionText')}
          </p>
        </div>

        <div className="mb-5">
          <Table
            columns={columnsOthers}
            data={data?.data ?? []}
            isLoadingResults={loading}
            loadingView={{
              title: t('surveyList.loadingText'),
              subtitle: t('surveyList.loadingSubText'),
            }}
            noResultsText={
              <TableEmptyContent
                title={t('surveyList.emptyText')}
                subtitle={t('surveyList.emptySubText')}
              />
            }
            pagination={{
              currentPage: query.page,
              totalItems: data?.total ?? 0,
              totalPages: data?.total_pages ?? 0,
              itemsPerPage: ITEMS_PER_PAGE_DEFAULT,
              onChangePage: (page: number) => updateQuery({ ...query, page }),
            }}
            footComponent={() => <Col style={{ minHeight: '70px' }} />}
          />
        </div>
      </Card>
      <ConfirmModal
        ref={confirmRef}
        onConfirm={onConfirm}
        onDecline={() => {}}
        message={confirmMessage}
      />
    </>
  );
}

export default SurveyTable;
